import { motion } from 'framer-motion';
import { useReveal } from '../components/useReveal';

const skills = ['React.js','WordPress','ERP Systems','REST APIs','VPS / Hostinger','Node.js','MySQL','SEO','Meta Ads','Social Media','UI / UX','Git'];

const expertise = [
  { title: 'Web Application Development', points: ['Scalable apps with React.js','ERP-style business systems','REST API & database integration','Performance-optimized interfaces'] },
  { title: 'E-commerce Development', points: ['WordPress WooCommerce stores','Product & order management','Payment workflow setup','Business-oriented optimization'] },
  { title: 'Deployment & Server Management', points: ['VPS setup on Hostinger','Production environment config','Application hosting & maintenance','Domain & server configuration'] },
  { title: 'Digital Business Solutions', points: ['Digital marketing for 4 businesses','Social media content strategy','Business growth via digital platforms','Real-world client experience'] },
];

function RevealCard({ children, delay = 0 }) {
  const { ref, controls, variants } = useReveal();
  return (
    <motion.div ref={ref} initial="hidden" animate={controls}
      variants={{ ...variants, visible: { ...variants.visible, transition: { ...variants.visible.transition, delay } } }}>
      {children}
    </motion.div>
  );
}

export default function About() {
  const { ref: titleRef, controls: titleControls, variants } = useReveal();

  return (
    <section id="about" style={{ background: 'var(--deep)', padding: '130px 80px' }}>

      <motion.div ref={titleRef} initial="hidden" animate={titleControls} variants={variants} className="section-header">
        <span className="section-num">01</span>
        <div className="section-line" />
        <h2 className="section-title">About <em>Me</em></h2>
      </motion.div>

      {/* Bio row — smaller photo left, text right */}
      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '72px', alignItems: 'start', marginBottom: '100px' }} className="about-grid">

        {/* Photo — smaller & contained */}
        <RevealCard delay={0.1}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '320px' }}>
            <div style={{ position: 'absolute', top: '-14px', right: '-14px', width: '100%', height: '100%', border: '1px solid rgba(201,169,110,0.15)', zIndex: 0, pointerEvents: 'none' }} />
            <div style={{ width: '100%', aspectRatio: '3/4', background: 'var(--panel)', border: '1px solid rgba(201,169,110,0.12)', position: 'relative', overflow: 'hidden', zIndex: 1 }}>
              {/* ✅ YOUR PHOTO — keep your existing img tag */}
              <img src="/vimal-about.webp" alt="Vimal Raj" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(to right, transparent, #c9a96e, transparent)' }} />
            </div>
            {/* Small badge */}
            <div style={{ marginTop: '16px', padding: '10px 16px', border: '1px solid rgba(201,169,110,0.2)', display: 'inline-block' }}>
              <div style={{ fontSize: '9px', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '2px' }}>Let’s Connect</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '14px', color: 'rgba(245,240,232,0.6)', fontWeight: 300 }}>vimalrajs.in</div>
            </div>
          </div>
        </RevealCard>

        {/* Bio text */}
        <RevealCard delay={0.25}>
          <div>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', fontWeight: 300, lineHeight: 1.7, color: 'rgba(245,240,232,0.9)', marginBottom: '24px' }}>
              Hello, I'm Vimal Raj — a Web Application Developer and Digital Solutions Specialist with hands-on experience building and deploying real-world business systems.
            </p>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '19px', fontWeight: 300, lineHeight: 1.75, color: 'rgba(245,240,232,0.65)', marginBottom: '22px' }}>
              I've developed four fully functional E-commerce websites using WordPress and built a custom ERP-level system with React for ST Gold Covering Company — streamlining operations through a modern web interface.
            </p>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '19px', fontWeight: 300, lineHeight: 1.75, color: 'rgba(245,240,232,0.65)', marginBottom: '22px' }}>
              Beyond development, I manage DevOps on Hostinger VPS servers — handling production setup, deployment, and live maintenance. I also run digital marketing for four business shops, bridging technology and marketing in real commercial environments.
            </p>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '19px', fontWeight: 300, lineHeight: 1.75, color: '#c9a96e', fontStyle: 'italic' }}>
              My goal: to grow as a full-stack developer & technology entrepreneur, delivering scalable solutions that solve real business problems.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '40px' }}>
              {skills.map(s => (
                <span key={s}
                  style={{ padding: '6px 16px', border: '1px solid rgba(255,255,255,0.07)', fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.45)', transition: 'all .3s' }}
                  onMouseEnter={e => { e.target.style.borderColor = '#c9a96e'; e.target.style.color = '#c9a96e'; }}
                  onMouseLeave={e => { e.target.style.borderColor = 'rgba(255,255,255,0.07)'; e.target.style.color = 'rgba(245,240,232,0.45)'; }}
                >{s}</span>
              ))}
            </div>
          </div>
        </RevealCard>
      </div>

      {/* Expertise Grid */}
      <RevealCard delay={0.1}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '48px' }}>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '13px', color: '#c9a96e', opacity: 0.7 }}>Core Expertise</span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2px' }} className="expertise-grid">
            {expertise.map((ex, i) => (
              <div key={ex.title}
                style={{ background: 'var(--panel)', padding: '36px 28px', border: '1px solid var(--border)', transition: 'border-color .3s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(201,169,110,0.25)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '42px', fontWeight: 300, color: 'rgba(201,169,110,0.1)', lineHeight: 1, marginBottom: '20px' }}>{String(i+1).padStart(2,'0')}</div>
                <div style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.04em', marginBottom: '18px' }}>{ex.title}</div>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {ex.points.map(p => (
                    <li key={p} style={{ fontSize: '12px', color: 'rgba(245,240,232,0.5)', lineHeight: 1.5, display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <span style={{ color: '#c9a96e', marginTop: '3px', fontSize: '8px', flexShrink: 0 }}>◆</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </RevealCard>

      <style>{`
        .expertise-grid { grid-template-columns: repeat(4, 1fr); }
        @media (max-width: 1100px) { .expertise-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 900px) {
          .about-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
          .expertise-grid { grid-template-columns: repeat(2, 1fr) !important; }
          section#about { padding: 90px 24px !important; }
        }
        @media (max-width: 560px) {
          .expertise-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}